/**
 * Security Center API - Using Generated Client
 * Sessions, devices, MFA/TOTP, passkeys, recovery codes and security events.
 * Each function unwraps the `{ success, ... }` envelope and returns the payload.
 */

import {
    getApiSecuritySessions,
    deleteApiSecuritySessions,
    postApiSecuritySessionsTerminateAll,
    getApiSecurityDevices,
    patchApiSecurityDevices,
    getApiSecurityMfaStatus,
    postApiSecurityMfaTotpSetup,
    postApiSecurityMfaTotpVerify,
    deleteApiSecurityMfaTotp,
    postApiSecurityMfaVerify,
    getApiSecurityPasskeys,
    getApiSecurityPasskeysRegistrationOptions,
    postApiSecurityPasskeysRegistration,
    deleteApiSecurityPasskeys,
    postApiSecurityPasskeysAuthenticationOptions,
    postApiSecurityPasskeysAuthentication,
    postApiSecurityRecoveryCodesGenerate,
    getApiSecurityRecoveryCodesStatus,
    postApiSecurityRecoveryCodesVerify,
    getApiSecurityEvents,
    getAxios,
} from '../../../generated/apiClient';

const withStepUp = (stepUpToken, options) => {
    if (stepUpToken) {
        return { headers: { 'X-Step-Up-Token': stepUpToken }, ...options };
    }
    return options;
};

export const getSessions = async (options) => {
    const res = await getApiSecuritySessions(options);
    return res?.sessions || [];
};

export const terminateSession = async (sessionId, stepUpToken) => {
    return await deleteApiSecuritySessions(sessionId, withStepUp(stepUpToken));
};

export const terminateAllSessions = async (stepUpToken) => {
    return await postApiSecuritySessionsTerminateAll(withStepUp(stepUpToken));
};

export const getDevices = async (options) => {
    const res = await getApiSecurityDevices(options);
    return res?.devices || [];
};

export const deviceAction = async (deviceId, action) => {
    return await patchApiSecurityDevices(deviceId, { action });
};

export const getMfaStatus = async (options) => {
    return await getApiSecurityMfaStatus(options);
};

export const startTotpSetup = async () => {
    return await postApiSecurityMfaTotpSetup();
};

export const verifyTotpSetup = async (code, name) => {
    return await postApiSecurityMfaTotpVerify({ code, name });
};

export const disableTotp = async (stepUpToken) => {
    return await deleteApiSecurityMfaTotp(withStepUp(stepUpToken));
};

export const verifyFactor = async (factor, code) => {
    return await postApiSecurityMfaVerify({ factor, code });
};

export const getPasskeys = async (options) => {
    const res = await getApiSecurityPasskeys(options);
    return res?.passkeys || [];
};

export const getPasskeyRegistrationOptions = async () => {
    const res = await getApiSecurityPasskeysRegistrationOptions();
    return res?.options;
};

export const registerPasskey = async (response, deviceName) => {
    return await postApiSecurityPasskeysRegistration({ response, deviceName });
};

export const removePasskey = async (passkeyId, stepUpToken) => {
    return await deleteApiSecurityPasskeys(passkeyId, withStepUp(stepUpToken));
};

export const getPasskeyAuthenticationOptions = async () => {
    const res = await postApiSecurityPasskeysAuthenticationOptions();
    return res?.options;
};

export const authenticateWithPasskey = async (response) => {
    return await postApiSecurityPasskeysAuthentication({ response });
};

export const generateRecoveryCodes = async (stepUpToken) => {
    return await postApiSecurityRecoveryCodesGenerate(withStepUp(stepUpToken));
};

export const getRecoveryCodesStatus = async (options) => {
    return await getApiSecurityRecoveryCodesStatus(options);
};

export const verifyRecoveryCode = async (code) => {
    return await postApiSecurityRecoveryCodesVerify({ factor: 'recovery', code });
};

export const getSecurityEvents = async (params = {}) => {
    const { type, limit, offset } = params;
    const res = await getApiSecurityEvents(type, limit, offset);
    return res?.events || [];
};

export const getDastineCredentials = async (options) => {
    const res = await getAxios().get('/api/security/dastine', options);
    return res.data?.credentials || res.data || [];
};

export const getDastineRegistrationOptions = async (stepUpToken) => {
    const res = await getAxios().post('/api/security/dastine/register/options', {}, withStepUp(stepUpToken));
    return res.data;
};

export const registerDastine = async (payload, stepUpToken) => {
    const res = await getAxios().post('/api/security/dastine/register/verify', payload, withStepUp(stepUpToken));
    return res.data;
};

export const removeDastine = async (dastineId, stepUpToken) => {
    const res = await getAxios().delete(`/api/security/dastine/${dastineId}`, withStepUp(stepUpToken));
    return res.data;
};

export default {
    getSessions,
    terminateSession,
    terminateAllSessions,
    getDevices,
    deviceAction,
    getMfaStatus,
    startTotpSetup,
    verifyTotpSetup,
    disableTotp,
    verifyFactor,
    getPasskeys,
    getPasskeyRegistrationOptions,
    registerPasskey,
    removePasskey,
    getPasskeyAuthenticationOptions,
    authenticateWithPasskey,
    generateRecoveryCodes,
    getRecoveryCodesStatus,
    verifyRecoveryCode,
    getSecurityEvents,
    getDastineCredentials,
    getDastineRegistrationOptions,
    registerDastine,
    removeDastine,
};